import React from 'react';
import PropTypes from 'prop-types';
import theme from '../Admin.scss';
import {getMessage as msg} from 'MessageBundle'
import BaseComponent from 'common/BaseComponent';
import Table from 'common/Table';
import deleteIcon from './delete.png';
import newItemIcon from './newItem.png';
import editIcon from './edit.png';
import update from 'immutability-helper';
import conf from 'configuration.json';
import {validate, required, isNotContainedIn, forEach, focusFirstError, findTableElement} from 'validation';
import ErrorBox from 'common/ErrorBox';
import ProgressBar from 'common/ProgressBar';
import Input from 'common/Input';
import Tooltip from 'common/Tooltip';

export default class LayoutFieldTab extends BaseComponent {

    constructor(props) {
        super(props);

        this.state = {
            layoutFields: [],
            newItem: {name: "", description: "", value: ""},
            dataChanged: false,
            processing: false,
            editIndexes: []
        };

        this.tableStructure = [
            {
                attribute: "name", label: <span>{msg("admin.layoutFieldTab.fieldName")} <Tooltip value={msg("admin.layoutFieldTab.name.title")}/></span>,
                title: msg("admin.layoutFieldTab.fieldName"), width: '251px',
                render: (row) => {
                    return <span>{row.name}</span>;
                }
            },
            {
                attribute: "description", label: msg("admin.layoutFieldTab.description"), title: msg("admin.layoutFieldTab.description.title"), width: '379px',
                headStyle: theme.columnCheck,
                render: (row, index) => {
                    return !this.state.editIndexes.includes(index) ? <span title={row.description}>{row.description}</span> :
                        <Input id={"description" + index} name="description" value={row.description} onChange={this.handleItemChange("description", index)}
                            index={index} errorMessages={this.state.errorMessages} title={row.description} showExclamationMark={false}/>
                }
            },
            {
                attribute: "value", label: msg("admin.layoutFieldTab.value"), width: '351px', title: msg("admin.layoutFieldTab.value.title"),
                headStyle: theme.columnCheck,
                render: (row, index) => {
                    return !this.state.editIndexes.includes(index) ? <span>{row.value}</span> :
                        <Input id={"value" + index} name="value" value={row.value} onChange={this.handleItemChange("value", index)}
                            index={index} errorMessages={this.state.errorMessages} showExclamationMark={false}/>
                }
            },
            {
                attribute: "action", label: msg("admin.layoutFieldTab.action"), title: msg("admin.layoutFieldTab.action.title"),
                width: '100px', headStyle: theme.columnAction, bodyStyle: theme.columnAction,
                render: (row, index) => {
                    return(
                        <div>
                            <a href="javascript:void(0)" onClick={() => this.chooseEditedItem(index)} title={msg('admin.layoutFieldTab.editIconText')}>
                                <img src={editIcon} alt=""/>
                            </a>
                            <a href="javascript:void(0)" onClick={() => this.deleteItem(index)} title={msg("admin.layoutFieldTab.deleteIconText")}>
                                <img src={deleteIcon} alt=""/>
                            </a>
                        </div>
                    );
                }
            }
        ];

        this.lastRowStructure = [
            {
                attribute: "name", label: msg("admin.layoutFieldTab.fieldName"), title: msg("admin.layoutFieldTab.name.title"), width: '251px',
                render: () => {
                    return <Input id="newItem-name" name="newName" type="text" allowedSymbols={/^[a-z0-9_]$/i} maxLength={20} value={this.state.newItem.name}
                        onChange={this.handleItemChange("name")} errorMessages={this.state.errorMessages} showExclamationMark={false}/>;
                }
            },
            {
                attribute: "description", label: msg("admin.layoutFieldTab.description"), title: msg("admin.layoutFieldTab.description.title"), width: '379px',
                headStyle: theme.columnCheck,
                render: () => {
                    return <Input id="newItem-description" name="newDescription" value={this.state.newItem.description} onChange={this.handleItemChange("description")}
                        errorMessages={this.state.errorMessages} showExclamationMark={false}/>;
                }
            },
            {
                attribute: "value", label: msg("admin.layoutFieldTab.value"), width: '351px', title: msg("admin.layoutFieldTab.value.title"),
                headStyle: theme.columnCheck,
                render: () => {
                    return <Input id="newItem-value" name="newValue" value={this.state.newItem.value} maxLength={500} onChange={this.handleItemChange("value")}
                        errorMessages={this.state.errorMessages} showExclamationMark={false}/>;
                }
            },
            {
                attribute: "action", label: msg("admin.layoutFieldTab.action"), title: msg("admin.layoutFieldTab.action.title"),
                width: '100px', headStyle: theme.columnAction, bodyStyle: theme.columnAction,
                render: () => {
                    return (
                        <a href="javascript:void(0)" onClick={this.addNewItem} title={msg("admin.layoutFieldTab.addIconText")}>
                            <img src={newItemIcon} alt=""/>
                        </a>
                    );
                }
            }
        ];

        this.validation = {
            common: [
                {
                    properties: ["state.newItem.name"],
                    individualNames: ["newName"],
                    strategies: [
                        required(msg('admin.layoutFieldTab.requiredError'))
                    ]
                }, {
                    properties: ["state.newItem.name", "state.layoutFields"],
                    individualNames: ["newName"],
                    strategies: [
                        isNotContainedIn("name", msg('admin.layoutFieldTab.duplicateError'))
                    ]
                }, {
                    properties: ["state.newItem.value"],
                    individualNames: ["newValue"],
                    strategies: [
                        required(msg('admin.layoutFieldTab.requiredError'))
                    ]
                }, {
                    properties: ["state.newItem.description"],
                    individualNames: ["newDescription"],
                    strategies: [
                        required(msg('admin.layoutFieldTab.requiredError'))
                    ]
                }
            ],
            save: [
                {
                    properties: ["state.layoutFields"],
                    strategies: [
                        forEach([
                            {
                                properties: ["description"],
                                individualNames: ["description"],
                                strategies: [
                                    required(msg('admin.layoutFieldTab.requiredError'))
                                ]
                            },
                            {
                                properties: ["value"],
                                individualNames: ["value"],
                                strategies: [
                                    required(msg('admin.layoutFieldTab.requiredError'))
                                ]
                            }
                        ])
                    ]
                }
            ]
        };

        this.elementOrder = {
            newName : "newItem-name",
            newDescription: "newItem-description",
            newValue: "newItem-value",
            description: findTableElement("description"),
            value: findTableElement("value")
        };
    }

    reloadData = () => {
        this.setState({processing: true});
        this.request('get', conf.rest.layoutFieldEndpoint).send((result) => {
            result.body.sort((a, b) => a.name.localeCompare(b.name));
            this.setState({
                layoutFields: result.body,
                processing: false,
                dataChanged: false,
                errorMessages: [],
                newItem: {name: "", description: "", value: ""},
                editIndexes: []
            });
        });
    };

    addNewItem = () => {
        const validationResult = validate(this, this.validation, ["common"]);
        if (validationResult.isEmpty()) {
            this.setState({
                layoutFields: update(this.state.layoutFields, {$push: [this.state.newItem]}),
                newItem: {name: "", description: "", value: ""},
                errorMessages: []
            })
        } else {
            let newErrorMessages = validationResult.getErrorMessages();
            this.setState({
                errorMessages: newErrorMessages
            });
            focusFirstError(this.elementOrder, newErrorMessages);
        }
    };

    deleteItem = (index) => {
        if(confirm(msg("admin.layoutFieldTab.delete.confirmation"))) {
            this.setState({
                layoutFields: update(this.state.layoutFields, {$splice: [[index, 1]]}),
                dataChanged: true
            })
        }
    };

    handleItemChange = (fieldName, index) => (e) => {
        if (index !== undefined) {
            const changedItem = update(this.state.layoutFields[index], {[fieldName]: {$set: e.target.value}});
            this.setState({
                layoutFields: update(this.state.layoutFields, {$splice: [[index, 1, changedItem]]})
            })
        } else {
            const changedItem = update(this.state.newItem, {[fieldName]: {$set: e.target.value}});
            this.setState({
                newItem: changedItem
            })
        }
    };

    chooseEditedItem = (index) => {
        this.setState({
            editIndexes: update(this.state.editIndexes, {$push: [index]})
        })
    };

    handleSave = () => {
        const validationResult = validate(this, this.validation, ["save"]);
        if (validationResult.isEmpty()) {
            this.setState({processing: true});
            this.request('post', conf.rest.layoutFieldEndpoint).params(this.state.layoutFields).send((response) => {
                response.body.sort((a, b) => a.name.localeCompare(b.name));
                this.setState({
                    layoutFields: response.body,
                    errorMessages: [],
                    processing: false,
                    dataChanged: false,
                    editIndexes: []
                })
            });
        } else {
            let newErrorMessages = validationResult.getErrorMessages();
            this.setState({
                errorMessages: newErrorMessages
            });
            focusFirstError(this.elementOrder, newErrorMessages);
        }
    };

    render() {
        return (
            <div className={theme.layoutFieldTab}>
                <ErrorBox groups={["common", "save"]} errorMessages={this.state.errorMessages}/>
                <div className={theme.tblWrap}>
                    <div className={theme.tblWrapInner}>
                        <Table structure={this.tableStructure} lastRowStructure={this.lastRowStructure}
                            value={this.state.layoutFields}
                            className={theme.layoutFieldTabTbl}/>
                    </div>
                </div>
                <div className={theme.btnGroupMini + " " + theme.btnAdmin}>
                    <span>{msg("admin.layoutTab.changeBtnsInfo")}</span>
                    <button onClick={this.reloadData}>{msg("admin.layoutFieldTab.skipChangesBtn")}</button>
                    <button className={theme.buttonAccent} onClick={this.handleSave}>{msg("admin.layoutFieldTab.saveChangesBtn")}</button>
                    <span className={theme.btnDecor}></span>
                    <button onClick={this.props.onFieldListClick} title={msg("admin.layoutFieldTab.dataList")}>{msg("admin.layoutFieldTab.openFieldlist")}</button>
                </div>
                <ProgressBar hidden={!this.state.processing}/>
            </div>
        );
    }
}

LayoutFieldTab.propTypes = {

    /**
     * JavaScript that has to be executed on Field List Button click.
     */
    onFieldListClick: PropTypes.func.isRequired
};


// WEBPACK FOOTER //
// ./src/dialogs/VPRekla/Admin/LayoutFieldTab/index.js